import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import debounce from 'lodash.debounce';
import OfferAssetPartnerTypeComponent from './OfferAssetPartnerTypeComponent';
import { Grid, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { supplierType } from '../constants';

const OfferAssetPartnerComponent = React.memo(
  ({
    t,
    partner,
    validationInfo,
    rights,
    onPropertyChange,
    loadDealers,
    loadSuppliers,
    localSupplier,
    loadPartnerContactInfo,
    isSearchingRestrictedDealer,
    shouldUseUsersAsContactPersonsForDealer,
    processing,
    addToRefs
  }) => {
    const onRadioButtonChange = onPropertyChange => event => {
      onPropertyChange(parseInt(event.target.value));
    };

    return (
      <Grid container>
        <OfferAssetPartnerTypeComponent
          partner={partner.dealer}
          shouldUseUsersAsContactPersonsForDealer={shouldUseUsersAsContactPersonsForDealer}
          onPropertyChange={onPropertyChange}
          label={t('OfferEdit.Asset.Partner.Dealer')}
          valueKey='dealerId'
          id='dealer'
          loadPartners={debounce(loadDealers, 500, { leading: true })}
          loadContactInfo={debounce(loadPartnerContactInfo('dealer'), 500, { leading: true })}
          validationInfo={validationInfo?.dealer || {}}
          processing={processing}
          isSearchingRestrictedDealer={isSearchingRestrictedDealer}
          addToRefs={addToRefs}
        />
        <Grid item xs={12} my={1}>
          <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <FormLabel sx={{ fontSize: 'medium', marginRight: 1 }}>{t('OfferEdit.Asset.Partner.SupplierIs')}</FormLabel>
            <RadioGroup
              sx={{ flexDirection: 'row' }}
              value={partner.supplierType}
              onChange={onRadioButtonChange(onPropertyChange('supplierType'))}
            >
              <FormControlLabel
                value={1}
                label={t('OfferEdit.Asset.Partner.Dealer')}
                control={<Radio disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing} />}
              />
              <FormControlLabel
                value={2}
                label={t('OfferEdit.Asset.Partner.Client')}
                control={<Radio disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing} />}
              />
              <FormControlLabel
                value={0}
                label={t('OfferEdit.Asset.Partner.Other')}
                control={<Radio disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing} />}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        {partner.supplierType === supplierType.Other && (
          <OfferAssetPartnerTypeComponent
            partner={localSupplier}
            shouldUseUsersAsContactPersonsForDealer={shouldUseUsersAsContactPersonsForDealer}
            onPropertyChange={onPropertyChange}
            label={t('OfferEdit.Asset.Partner.Supplier')}
            valueKey='supplierId'
            id='supplier'
            processing={processing}
            loadPartners={debounce(loadSuppliers, 500, { leading: true })}
            loadContactInfo={debounce(loadPartnerContactInfo('supplier'), 500, { leading: true })}
            validationInfo={validationInfo?.supplier || {}}
            addToRefs={addToRefs}
          />
        )}
      </Grid>
    );
  }
);

OfferAssetPartnerComponent.displayName = 'OfferAssetPartnerComponent';

OfferAssetPartnerComponent.propTypes = {
  t: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  validationInfo: PropTypes.object,
  loadDealers: PropTypes.func.isRequired,
  loadSuppliers: PropTypes.func.isRequired,
  loadPartnerContactInfo: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool,
  shouldUseUsersAsContactPersonsForDealer: PropTypes.bool.isRequired,
  localSupplier: PropTypes.object,
  processing: PropTypes.bool,
  addToRefs: PropTypes.func.isRequired
};

const MemoizedOfferAssetPartnerComponent = compose(withTranslation())(withRights(OfferAssetPartnerComponent));

const ForwardedOfferAssetPartnerComponent = forwardRef((props, ref) => (
  <MemoizedOfferAssetPartnerComponent {...props} forwardedRef={ref} />
));

ForwardedOfferAssetPartnerComponent.displayName = 'ForwardedOfferAssetPartnerComponent';

export default ForwardedOfferAssetPartnerComponent;
